import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { MenuItem, MessageService } from 'primeng/api';
import { filter, Subject, takeUntil } from 'rxjs';
import { ApiService } from 'src/app/_utils/api/api.service';
import { StorageService } from 'src/app/_utils/storage/storage.service';
import { TranslatorService } from 'src/app/_utils/translator/translator.service';
import { UtilityService } from 'src/app/_utils/utility/utility.service';
import { LANGS } from 'src/app/app.config';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  providers: [MessageService]
})
export class TopbarComponent implements OnInit, OnDestroy {
  profileOptions: MenuItem[] = [];
  langOptions: MenuItem[] = [];
  isFullScreen: boolean = false;
  assistants: any[] = [];
  maxAssistants: number = 0;
  selectedAssistant: any = null;
  user: any = {};
  isStaging: boolean = environment.staging;
  avatarLabel: string | null = null;
  activeLang: string;
  userHasPluginAvailable: boolean = false;
  sidebarVisible: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(protected translator: TranslatorService, private translocoService: TranslocoService, private renderer: Renderer2, private messageService: MessageService, private apiService: ApiService, protected router: Router, private storageService: StorageService, private route: ActivatedRoute) {
    this.user = this.storageService.get('user');
    this.avatarLabel = this.storageService.get('user')?.name?.charAt(0).toUpperCase();
    this.activeLang = this.translocoService.getActiveLang();
    this.translocoService.selectTranslate(['error', 'error_updating_lang', 'profile.account', 'profile.billing', 'logout', 'support', 'profile.usage']).subscribe(translations => {
      this.profileOptions = [
        {
          label: translations[2],
          command: (event) => this.changePage('/user/profile', undefined, false)
        },
        {
          label: translations[3],
          disabled: this.user?.admin != undefined && this.user?.admin == true,
          command: (event) => this.changePage('/user/profile', 'billing', false)
        },
        {
          label: translations[6],
          disabled: this.user?.admin != undefined && this.user?.admin == true,
          command: (event) => this.changePage('/user/profile', 'usage', false)
        },
        {
          label: translations[5],
          disabled: true
        },
        {
          separator: true
        },
        {
          label: translations[4],
          command: (event) => {
            this.storageService.delete('user');
            this.router.navigate(['/auth/login']);
          }
        }
      ];

      let generateMenuItemHtml = (lang: string, label: string) => {
        return {
          label: `<span class="flex align-items-center"><img src="/assets/flags/${lang}.svg" width="22" alt="${lang} flag" draggable="false"><span class="ml-2">${label}</span></span>`,
          escape: false,
          disabled: this.translocoService.getActiveLang() == lang,
          command: (event: any) => {
            this.translocoService.setActiveLang(lang); // user clicked on a language button
            this.apiService.updateUserInfo({ lang: lang }).subscribe(response => {
              if (!response.success || response.error) {
                console.error(response);
                this.messageService.add({ severity: 'error', summary: translations[0], detail: translations[1] });
              }
            });
          }
        }
      }

      this.translocoService.langChanges$.subscribe(lang => {
        this.activeLang = lang;
        this.langOptions = [];
        // this is used to refresh active lang on menu dropdown
        for (let i = 0; i < LANGS.length; i++) {
          this.langOptions.push(generateMenuItemHtml(LANGS[i].code, LANGS[i].label))
        }
      });
    });
  }

  ngOnInit(): void {
    //TODO: evitare di fare una doppia richiesta per ottenere i chatbot

    if (this.user?.admin == true) { // admin does not have chatbots
      return;
    }

    /*this.translocoService.selectTranslate(['error', 'error_fetching_data']).subscribe(translations => {
      this.utilityService.getAssistants().subscribe({
        next: (data) => {
          this.assistants = data;

          for (let i = 0; i < this.assistants.length; i++) {
            if (this.router.url.includes(this.assistants[i].uuid))
              this.assistantName = this.assistants[i].name;
          }

          this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
            let status: boolean = false;
            for (let i = 0; i < this.assistants.length; i++) {
              if (event.urlAfterRedirects.includes(this.assistants[i].uuid)) {
                this.assistantName = this.assistants[i].name;
                status = true;
                break;
              }
              if (!status) this.assistantName = null;
            }
          });
        },
        error: (error) => {
          this.messageService.add({ severity: 'error', summary: translations[0], detail: error.message || translations[1] });
          console.error(error);
        },
      });
    });*/

    const translate$ = this.translocoService.selectTranslate(['error', 'error_fetching_data']).pipe(takeUntil(this.destroy$)).subscribe(translations => {
      this.apiService.getChatBots().subscribe((response) => {
        if (response.success) {
          this.assistants = response.data.assistants;
          this.maxAssistants = response.data.max_assistants;
          this.userHasPluginAvailable = response.data.has_plugin_available;

          for (let i = 0; i < this.assistants.length; i++) {
            if (this.router.url.includes(this.assistants[i].uuid))
              this.selectedAssistant = this.assistants[i];
          }

          this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
            let status: boolean = false;
            for (let i = 0; i < this.assistants.length; i++) {
              if (event.urlAfterRedirects.includes(this.assistants[i].uuid)) {
                this.selectedAssistant = this.assistants[i];
                status = true;
                break;
              }
              if (!status) this.selectedAssistant = null;
            }
            translate$.unsubscribe();
          });
        } else {
          this.messageService.add({ severity: 'error', summary: translations[0], detail: translations[1] });
          console.error(response);
        }
        translate$.unsubscribe();
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  openChat() {
    const uuid = this.router.url.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    if (uuid) {
      window.open(environment.chatUrl + '/' + uuid, '_blank');
    } else {
      console.error('uuid not found');
    }
  }

  changePage(page: string, fragment: string | undefined = undefined, checkUUID: boolean = true) {
    if (checkUUID) {
      const uuid = this.router.url.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
      if (uuid) {
        this.router.navigate(['chatbot', uuid[0], page], { fragment: fragment });
      } else {
        console.error('uuid not found');
      }
    } else {
      this.router.navigate([page], { fragment: fragment });
    }
    this.sidebarVisible = false;
  }

  urlContainsUuid(): boolean {
    return this.router.url.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/) !== null
  }

  /**
   * Toggles the full-screen mode for the document.
   * 
   * The method supports the standard `requestFullscreen` and `exitFullscreen` methods,
   * as well as the vendor-prefixed methods for Mozilla and WebKit browsers.
   * 
   * @remarks
   * This method modifies the `isFullScreen` property to reflect the current full-screen state.
   */
  toggleFullScreen(): void {
    let document: any = window.document;

    if (!document.fullscreenElement && /* alternative standard method */ !document.mozFullScreenElement && !document.webkitFullscreenElement) {  // current working methods
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen();
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
    this.isFullScreen = !this.isFullScreen;
  }
}
